import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, User } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = '/api';

const AVATAR_URL = 'https://static.prod-images.emergentagent.com/jobs/073da548-8b42-4c53-baa6-6ea4ddd4386f/images/59b2124bf4b891ddfc3c7d7bc2e57c3057c109904ed77bb821e951e5df038995.jpeg';
const LOAN_PAGES = ['/demander-un-pret-auto', '/loan-request', '/pret-auto-2e-chance', '/pret-auto-3e-chance'];

const TypingDots = () => (
  <div className="flex justify-start">
    <div className="flex items-end gap-2">
      <img src="https://static.prod-images.emergentagent.com/jobs/073da548-8b42-4c53-baa6-6ea4ddd4386f/images/59b2124bf4b891ddfc3c7d7bc2e57c3057c109904ed77bb821e951e5df038995.jpeg" alt="Alex" className="w-6 h-6 rounded-full object-cover" />
      <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex items-center gap-1">
        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
      </div>
    </div>
  </div>
);

const ChatWidget = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => {
    // Conversation memory: check if returning visitor
    const saved = localStorage.getItem('car_chat_session');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Reuse session if less than 24 hours old
        if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return parsed.id;
        }
      } catch {}
    }
    const newId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('car_chat_session', JSON.stringify({ id: newId, timestamp: Date.now() }));
    return newId;
  });
  const [showNudge, setShowNudge] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [typingGreeting, setTypingGreeting] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const lastUserMsgTime = useRef(null);
  const silenceTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages, typingGreeting, showTyping]);

  // Scroll-triggered nudge — show after 50% scroll on non-loan pages
  useEffect(() => {
    if (isOpen || nudgeDismissed || LOAN_PAGES.includes(location.pathname)) return;

    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 50 && !showNudge) {
        setShowNudge(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen, nudgeDismissed, showNudge, location.pathname]);

  // Hide nudge when chat opens
  useEffect(() => {
    if (isOpen) setShowNudge(false);
  }, [isOpen]);

  // Listen for external open event (from mobile sticky bar)
  useEffect(() => {
    const handleOpenChat = () => setIsOpen(true);
    window.addEventListener('open-chat-widget', handleOpenChat);
    return () => window.removeEventListener('open-chat-widget', handleOpenChat);
  }, []);

  // Send abandon notification on page unload if chat was active
  useEffect(() => {
    const handleUnload = () => {
      const userMsgCount = messages.filter(m => m.role === 'user').length;
      if (userMsgCount >= 1 && isOpen) {
        navigator.sendBeacon(`${API}/chat/abandon`, JSON.stringify({
          session_id: sessionId, message: '', channel: 'web'
        }));
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [messages, isOpen, sessionId]);

  // Typing animation before greeting — or load existing session
  useEffect(() => {
    if (!isOpen || messages.length > 0) return;

    // Check for existing session history (returning visitor)
    const loadHistory = async () => {
      try {
        const { data } = await axios.get(`${API}/chat/history/${sessionId}`);
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages.map(m => ({ role: m.role, content: m.content })));
          // Add welcome back message
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: language === 'en'
              ? 'Welcome back! We had started looking at your file. Want to continue where we left off?'
              : 'Content de vous revoir! On avait commencé à regarder votre dossier. On continue où on en était?'
          }]);
          if (inputRef.current) inputRef.current.focus();
          return;
        }
      } catch {}

      // New session — show greeting with typing animation
      setTypingGreeting(true);
      setTimeout(() => {
        setTypingGreeting(false);
        setMessages([{
          role: 'assistant',
          content: language === 'en'
            ? 'Hi and welcome! I\'m Alex. I know reaching out isn\'t always easy, but you\'re in the right place. We\'re here to listen and help you find a solution — no judgment, no pressure. What\'s your name?'
            : 'Bonjour et bienvenue! Moi c\'est Alex. Je sais que faire le premier pas n\'est pas toujours facile, mais vous êtes au bon endroit. On est là pour vous écouter et vous accompagner, sans jugement. À qui ai-je l\'honneur?'
        }]);
        if (inputRef.current) inputRef.current.focus();
      }, 3000);
    };

    loadHistory();
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setSending(true);

    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: userMsg,
        channel: 'web'
      });
      
      const response = data.response;
      
      // Show typing indicator with realistic delay
      setShowTyping(true);
      const typingDelay = Math.min(1000 + response.length * 12, 3500);
      const jitter = (Math.random() - 0.5) * 400;
      await new Promise(r => setTimeout(r, typingDelay + jitter));
      setShowTyping(false);
      
      // Split into 2 bubbles if response has a natural break point
      const splitMatch = response.match(/^(.+?[.!])\s+((?:Et |Ensuite|Juste|On |De votre|Côté|Avez|Quel|Votre|Vous |Est-ce|Par curiosité|Combien|Depuis|Avec ).+)$/s);
      
      if (splitMatch && splitMatch[1].length > 10 && splitMatch[2].length > 10) {
        setMessages(prev => [...prev, { role: 'assistant', content: splitMatch[1].trim() }]);
        setShowTyping(true);
        const gap = 600 + Math.random() * 500;
        await new Promise(r => setTimeout(r, gap));
        setShowTyping(false);
        setMessages(prev => [...prev, { role: 'assistant', content: splitMatch[2].trim() }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      }

      // Start silence timer — nudge after 2 min of no user reply
      lastUserMsgTime.current = Date.now();
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        const userMsgCount = messages.filter(m => m.role === 'user').length;
        if (userMsgCount >= 1 && Date.now() - lastUserMsgTime.current >= 115000) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: language === 'en'
              ? 'Still there? No pressure — I\'m here if you have any questions.'
              : 'Toujours là? Pas de pression, je reste disponible si vous avez des questions.'
          }]);
        }
      }, 120000);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: language === 'en'
          ? 'Sorry, I\'m having trouble right now. Please try again or call us at 514-418-3968.'
          : 'Désolé, j\'ai un problème technique. Veuillez réessayer ou appelez-nous au 514-418-3968.'
      }]);
    }
    setSending(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Notify backend if customer had a conversation going (2+ user messages)
    const userMsgCount = messages.filter(m => m.role === 'user').length;
    if (userMsgCount >= 1) {
      axios.post(`${API}/chat/abandon`, {
        session_id: sessionId,
        message: '',
        channel: 'web'
      }).catch(() => {});
    }
  };

  return (
    <>
      {/* Collapsed: Speech Bubble + Avatar + CTA */}
      {!isOpen && (
        <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 hidden md:flex flex-col items-end gap-0" data-testid="chat-widget-collapsed">
          {/* Speech Bubble */}
          <div className="relative mb-2 animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '0.5s', animationFillMode: 'both' }}>
            <button
              onClick={() => { setNudgeDismissed(true); }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center shadow-sm border border-gray-200 z-10 transition-colors"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5 text-gray-500" />
            </button>
            {!nudgeDismissed && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 px-5 py-4 max-w-[260px] cursor-pointer" onClick={() => setIsOpen(true)}>
                <p className="text-sm text-gray-800 leading-relaxed">
                  {language === 'en'
                    ? 'Hello 😊 I\'m online to answer your questions!'
                    : 'Bonjour 😊 Je suis en ligne pour répondre à vos questions!'}
                </p>
                {/* Triangle tail */}
                <div className="absolute -bottom-2 right-12 w-4 h-4 bg-white border-b border-r border-gray-200 rotate-45"></div>
              </div>
            )}
          </div>

          {/* Green CTA Button with Avatar */}
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2 bg-[#1a5c3b] hover:bg-[#165432] text-white pl-2 pr-5 py-2 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 group"
            data-testid="chat-widget-toggle"
          >
            <img
              src={AVATAR_URL}
              alt="Alex"
              className="w-10 h-10 rounded-full border-2 border-white/30 object-cover"
            />
            <span className="font-semibold text-sm">
              {language === 'en' ? 'Let\'s chat' : 'Discutons ensemble'}
            </span>
            <MessageCircle className="w-5 h-5 text-white/80 group-hover:text-white transition-colors" />
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-50 w-full md:w-[380px] h-[100dvh] md:h-[520px] md:rounded-2xl overflow-hidden shadow-2xl border border-gray-200 flex flex-col bg-white" data-testid="chat-widget-window">
          {/* Header */}
          <div className="bg-[#1a5c3b] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <img src={AVATAR_URL} alt="Alex" className="w-9 h-9 rounded-full border-2 border-white/30 object-cover" />
              <div>
                <h3 className="text-white font-bold text-sm">Alex</h3>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <span className="text-green-200 text-xs">{language === 'en' ? 'Online' : 'En ligne'}</span>
                </div>
              </div>
            </div>
            <button onClick={handleClose} className="text-white/70 hover:text-white p-1" data-testid="chat-widget-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
            {typingGreeting && <TypingDots />}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-end gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${
                    msg.role === 'user' ? 'bg-gray-300' : ''
                  }`}>
                    {msg.role === 'user'
                      ? <User className="w-3.5 h-3.5 text-gray-600" />
                      : <img src={AVATAR_URL} alt="Alex" className="w-6 h-6 rounded-full object-cover" />
                    }
                  </div>
                  <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#1a5c3b] text-white rounded-br-sm'
                      : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              </div>
            ))}
            {(sending || showTyping) && <TypingDots />}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 px-3 py-3 bg-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={language === 'en' ? 'Type your message...' : 'Écrivez votre message...'}
                className="flex-1 bg-gray-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#1a5c3b]/30 border border-transparent focus:border-[#1a5c3b]/30"
                disabled={sending}
                data-testid="chat-input"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                className="w-10 h-10 bg-[#1a5c3b] hover:bg-[#165432] text-white rounded-full flex items-center justify-center disabled:opacity-40 transition-all"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
